// Updated File: src/components/SearchBar.jsx
// Enhanced for perfect Header integration with improved responsive design
// - Optimized for desktop (max 600px) and mobile (100% width) layouts
// - Better dropdown positioning and mobile behavior
// - Improved keyboard navigation and accessibility
// - Smoother animations and transitions
// - Consistent with ReyZa brand styling

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { productsAPI } from '../services/api';
import { Search, Loader2, AlertTriangle, ShoppingBag, X } from 'lucide-react';
import './css/SearchBar.css';

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const getProductPath = (product) => `/product/${product.slug || product.id}`;

  // Debounce function to delay API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch suggestions
  const fetchSuggestions = useCallback(async (term) => {
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      setLoading(true);
      const response = await productsAPI.search(term);
      // Take first 5 suggestions
      setSuggestions(response.data?.slice(0, 5) || []);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced fetch
  const debouncedFetchSuggestions = useMemo(
    () => debounce((term) => fetchSuggestions(term), 300),
    [fetchSuggestions]
  );

  useEffect(() => {
    debouncedFetchSuggestions(searchTerm);
  }, [searchTerm, debouncedFetchSuggestions]);

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && searchTerm.length > 0);
  }, [suggestions, searchTerm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      inputRef.current?.blur();
    }
  };

  const handleSuggestionClick = (product) => {
    navigate(getProductPath(product));
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedIndex(-1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Enhanced keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
      inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        handleSearchSubmit(e);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    }
  };

  // Helper to get display price (prefer salePrice if available, else regularPrice)
  const getDisplayPrice = useCallback((product) => {
    const price = product.salePrice || product.regularPrice;
    return price ? parseFloat(price).toFixed(2) : '0.00';
  }, []);

  // Memoized suggestions for better performance
  const memoizedSuggestions = useMemo(() => suggestions.map((product, index) => (
    <div
      key={product.id}
      className={`suggestion-item ${selectedIndex === index ? 'selected' : ''}`}
      onMouseEnter={() => setSelectedIndex(index)}
      onMouseDown={(e) => {
        e.preventDefault();
        handleSuggestionClick(product);
      }}
      role="option"
      aria-selected={selectedIndex === index}
    >
      <Link
        to={getProductPath(product)}
        className="suggestion-link"
        onClick={(e) => e.preventDefault()}
      >
        <div className="suggestion-image-wrapper">
          <img
            src={product.imageUrl || '/placeholder-image.jpg'}
            alt={product.name}
            className="suggestion-image"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="suggestion-info">
          <h4 className="suggestion-name">{product.name}</h4>
          <p className="suggestion-price">DT {getDisplayPrice(product)}</p>
        </div>
      </Link>
    </div>
  )), [suggestions, selectedIndex, getDisplayPrice, getProductPath]);

  return (
    <div className="search-container" ref={searchRef}>
      <form onSubmit={handleSearchSubmit} className="search-form" role="search">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={20} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setShowSuggestions(suggestions.length > 0)}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher des produits..."
            className="search-input"
            aria-label="Rechercher des produits"
            aria-autocomplete="list"
            aria-controls="search-suggestions"
            aria-expanded={showSuggestions}
            autoComplete="off"
          />
          {searchTerm && !loading && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="search-clear"
              aria-label="Effacer la recherche"
            >
              <X size={16} />
            </button>
          )}
          {loading && <Loader2 className="search-loading" size={18} aria-label="Chargement" />}
        </div>
        <button type="submit" className="search-button" aria-label="Lancer la recherche">
          <Search size={18} />
        </button>
      </form>

      {showSuggestions && (
        <div 
          className="suggestions-dropdown" 
          id="search-suggestions"
          role="listbox" 
          aria-label="Suggestions de recherche"
        >
          {loading && (
            <div className="suggestion-item suggestion-loading" role="option">
              <Loader2 className="loading-spinner" size={18} />
              <span>Recherche de produits...</span>
            </div>
          )}
          {!loading && suggestions.length === 0 && searchTerm.length >= 2 && (
            <div className="suggestion-item suggestion-no-results" role="option">
              <AlertTriangle size={18} className="no-results-icon" />
              <span>Aucun produit trouve pour "{searchTerm}"</span>
            </div>
          )}
          {!loading && suggestions.length > 0 && (
            <>
              <div className="suggestions-list">
                {memoizedSuggestions}
              </div>
              <div className="suggestion-footer">
                <Link
                  to={`/search?q=${encodeURIComponent(searchTerm)}`}
                  className="view-all-link"
                  onClick={() => {
                    setShowSuggestions(false);
                    setSelectedIndex(-1);
                  }}
                >
                  <ShoppingBag size={16} />
                  <span>Voir tous les resultats pour "{searchTerm}"</span>
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
